/**
 * The resource ledger: every change of a player's gold and diamonds that did not come from the game itself but from an
 * operator (the console, POST /admin, the CLI) — grants, take-aways, state edits and progress resets. It is the record
 * that tells an honest balance from an explained one: the anti-cheat reads it (`list`, `totals`, `onStored`) to know
 * which part of a player's balance an operator put there or took away, and when.
 *
 * Each change becomes one document of the collection `resource_ledger` (id = zero-padded time + sequence + random
 * tail: ids sort by time), written in the SAME storage batch as the canonical game state it changed
 * (ProfileService.adminSetState `extra`, ProfileService.commitCanonical): the state and its ledger entry are stored
 * together or not at all, under the player's profile lock, so the entries of a player are in the order of its
 * revisions and `after` of one entry is `before` of the next operator change. An entry holds the player (by id), what
 * kind of change it was, the revision it produced, the balances before and after and their difference (`delta`, signed),
 * what the operator asked for when it differs (a take-away clamped at 0: `requested`), the items of an item grant, the
 * operator (id, username or 'service', role, how) and the operator's reason. Nothing else of the player (no name, no
 * game state). The entries are kept as long as the account (operators change balances rarely: no pruning) and go with
 * it (account deletion: `deletionOps`). The operator audit (admin/audit.ts) keeps its own entry of the same action.
 */
import type { JsonObject } from '../../../shared/json';
import type { PlayerId } from '../../../shared/protocol/model';
import { randomId } from '../../../shared/random';
import { decodeState } from '../../../game/rules/index';
import { type StorageBatchOp, deleteOp, putOp } from '../storage';
import { C, type ServiceDeps } from './deps';

/** Gold and diamonds of a game state. */
export interface LedgerCurrencies {
  [field: string]: number;
  gold: number;
  diam: number;
}

/** Who made a change: 'admin' = an operator (console, POST /admin, CLI). Other sources may follow. */
export type LedgerSource = 'admin';

/**
 * What kind of change: 'grant' (players.grant: gold, diamonds or items), 'revoke' (players.revoke: a take-away),
 * 'edit' (players.editState changed a balance), 'reset' (players.resetProgress changed a balance).
 */
export type LedgerKind = 'grant' | 'revoke' | 'edit' | 'reset';

export const LEDGER_KINDS: readonly LedgerKind[] = ['grant', 'revoke', 'edit', 'reset'];

/** The operator of a change (AdminCaller without its session). */
export interface LedgerActor {
  [field: string]: string | null;
  operatorId: string | null;
  /** The operator's username, or 'service' for POST /admin and the CLI. */
  operator: string;
  role: string;
  via: 'console' | 'http' | 'cli';
}

/** Items of an item grant. */
export interface LedgerItem {
  [field: string]: string | number;
  category: string;
  id: string;
  count: number;
}

export interface ResourceLedgerEntry {
  entryId: string;
  at: number;
  playerId: PlayerId;
  source: LedgerSource;
  kind: LedgerKind;
  /** The profile revision the change produced. */
  revision: number;
  before: LedgerCurrencies;
  after: LedgerCurrencies;
  /** after - before (negative: taken away). */
  delta: LedgerCurrencies;
  /** What the operator asked to take (revoke), or null; `delta` is smaller when the balance was smaller. */
  requested: LedgerCurrencies | null;
  /** The items of an item grant (empty otherwise). */
  items: LedgerItem[];
  actor: LedgerActor;
  reason: string | null;
}

export type NewLedgerEntry = Omit<ResourceLedgerEntry, 'entryId' | 'at' | 'delta' | 'requested' | 'items'> & { at?: number; requested?: LedgerCurrencies | null; items?: LedgerItem[] };

/** Sums over ledger entries: what operators added, what they removed (both positive) and the net change. */
export interface LedgerTotals {
  entries: number;
  added: LedgerCurrencies;
  removed: LedgerCurrencies;
  net: LedgerCurrencies;
}

export interface LedgerQuery {
  playerId?: PlayerId;
  source?: LedgerSource;
  kinds?: readonly LedgerKind[];
  /** Entries at or after this time (epoch ms). */
  from?: number;
  /** Entries at or before this time (epoch ms). */
  to?: number;
  limit?: number;
}

/**
 * Gold and diamonds of a stored game state (the rules codec reads the ProtectedInt fields), or null when the state has
 * none the rules can read. Reads only these two fields, so a list of every player stays cheap.
 */
export function currenciesOf(gameState: unknown): LedgerCurrencies | null {
  if (!gameState || typeof gameState !== 'object' || Array.isArray(gameState)) return null;
  const g = gameState as Record<string, unknown>;
  const p = g.player;
  if (!p || typeof p !== 'object' || Array.isArray(p)) return null;
  try {
    const { player } = decodeState({ format: g.format, player: { gold: (p as Record<string, unknown>).gold, diam: (p as Record<string, unknown>).diam }, map_info: [] });
    const gold = Number(player.gold);
    const diam = Number(player.diam);
    return Number.isFinite(gold) && Number.isFinite(diam) ? { gold, diam } : null;
  } catch {
    return null;
  }
}

const zero = (): LedgerCurrencies => ({ gold: 0, diam: 0 });

export type LedgerListener = (entry: ResourceLedgerEntry) => void;

export class ResourceLedger {
  private seq = 0;
  private readonly listeners = new Set<LedgerListener>();

  constructor(private readonly d: ServiceDeps) {}

  /** Calls `l` with every entry once its batch is stored (the anti-cheat's live feed). Returns the unsubscribe. */
  onStored(l: LedgerListener): () => void {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  }

  /** The entry and its storage write, for the batch of the state change it records; then `stored(entry)`. */
  prepare(e: NewLedgerEntry): { entry: ResourceLedgerEntry; op: StorageBatchOp } {
    const at = e.at ?? this.d.clock.now();
    const entryId = `${String(Math.max(0, Math.floor(at))).padStart(15, '0')}-${String(this.seq++ % 1_000_000).padStart(6, '0')}-${randomId(this.d.rng, '', 5)}`;
    const entry: ResourceLedgerEntry = {
      entryId,
      at,
      playerId: e.playerId,
      source: e.source,
      kind: e.kind,
      revision: e.revision,
      before: { gold: e.before.gold, diam: e.before.diam },
      after: { gold: e.after.gold, diam: e.after.diam },
      delta: { gold: e.after.gold - e.before.gold, diam: e.after.diam - e.before.diam },
      requested: e.requested ? { gold: e.requested.gold, diam: e.requested.diam } : null,
      items: (e.items ?? []).map((i) => ({ category: i.category, id: i.id, count: i.count })),
      actor: { operatorId: e.actor.operatorId, operator: e.actor.operator, role: e.actor.role, via: e.actor.via },
      reason: e.reason,
    };
    return { entry, op: putOp(C.resourceLedger, entryId, entry) };
  }

  /** After the batch of a prepared entry is stored: its log line and the listeners. */
  stored(entry: ResourceLedgerEntry): void {
    this.d.logger.info('resource ledger', {
      playerId: entry.playerId,
      source: entry.source,
      kind: entry.kind,
      revision: entry.revision,
      gold: entry.delta.gold,
      diam: entry.delta.diam,
      operator: entry.actor.operator,
      entryId: entry.entryId,
    });
    for (const l of this.listeners) {
      try {
        l(entry);
      } catch (err) {
        this.d.logger.error('resource ledger listener failed', { entryId: entry.entryId, err });
      }
    }
  }

  /** Entries matching the query, newest first, at most `limit` (default 100, at most 10 000). */
  async list(q: LedgerQuery = {}): Promise<ResourceLedgerEntry[]> {
    const all = await this.d.storage.list<ResourceLedgerEntry>(C.resourceLedger);
    return all
      .filter(
        (e) =>
          (q.playerId === undefined || e.playerId === q.playerId) &&
          (q.source === undefined || e.source === q.source) &&
          (q.kinds === undefined || q.kinds.includes(e.kind)) &&
          (q.from === undefined || e.at >= q.from) &&
          (q.to === undefined || e.at <= q.to),
      )
      .sort((a, b) => (a.entryId < b.entryId ? 1 : a.entryId > b.entryId ? -1 : 0))
      .slice(0, Math.max(1, Math.min(q.limit ?? 100, 10_000)));
  }

  /** The sums of a player's entries (optionally of a time range, source or kinds). */
  async totals(playerId: PlayerId, q: Omit<LedgerQuery, 'playerId' | 'limit'> = {}): Promise<LedgerTotals> {
    const entries = await this.list({ ...q, playerId, limit: 10_000 });
    const out: LedgerTotals = { entries: entries.length, added: zero(), removed: zero(), net: zero() };
    for (const e of entries) {
      for (const k of ['gold', 'diam'] as const) {
        const d = e.delta[k];
        if (d > 0) out.added[k] += d;
        else out.removed[k] -= d;
        out.net[k] += d;
      }
    }
    return out;
  }

  /** The player's entries (account deletion). */
  async deletionOps(playerId: PlayerId): Promise<StorageBatchOp[]> {
    const all = await this.d.storage.list<ResourceLedgerEntry>(C.resourceLedger);
    return all.filter((e) => e?.playerId === playerId).map((e) => deleteOp(C.resourceLedger, e.entryId));
  }
}

/** A ledger entry as plain JSON (the console's tables). */
export function ledgerEntryJson(e: ResourceLedgerEntry): JsonObject {
  return JSON.parse(JSON.stringify(e)) as JsonObject;
}
